import React, { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Moment } from 'moment';
import {
    Chip,
    Grid,
    Icon,
    Menu,
    RangeBlocks,
    Typography,
    useTheme,
} from '../../../../../base-components';
import { IActiveFilter, IFilterOption } from '../../../../table.interfaces';
import { IOption } from '../../../../../base-forms';
import DateRangeMenu from './DateRangeMenu';
import SelectorMenu from './SelectorMenu';
import { FilterTypes } from '../../../../table.consts';
import { useFormContext } from 'react-hook-form';
import {
    Directions,
    IRangeBlocksValues,
    ITableForm,
    TableFormValues,
} from '../../../../../../common';
import { renderDateRangeLabel } from './filters.utils';
import { useTranslation } from 'react-i18next';
import { RangeBlockChipWrapper, StyledChip } from './filters.styles';
import ScoreRangeMenu from './ScoreRangeMenu';
import NumberRangeMenu from './NumberRangeMenu';
import FiltersMenu from './FiltersMenu';
import BooleanFilterMenu from './BooleanFilterMenu';
import TagsFilterMenu from './TagsMenu';
import { ITag } from '../../../../../../common/interfaces';
import PargodaTag from '../../../../../base-components/PargodaTagsEditor/PargodaTag';
import { Box } from '../../../../../base-components';

interface IFiltersProps {
    filters: Array<IFilterOption>;
}

const FilterComponent = (props: IFiltersProps) => {
    const { watch, setValue } = useFormContext<ITableForm>();
    const { t } = useTranslation();
    const theme = useTheme();
    const { filters = [] } = props;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [valueAnchorEl, setValueAnchorEl] = useState<null | HTMLElement>(
        null
    );
    const [activeFilterIndex, setActiveFilterIndex] = useState<number | null>(
        null
    );

    const activeFilters: Array<IActiveFilter> = watch(
        TableFormValues.filters
    ) as Array<IActiveFilter>;
    console.log(activeFilters);

    const handleAddFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleFilterSelect = (filter: IFilterOption) => {
        const newFilterIndex = activeFilters.length;
        const updatedFilters: Array<IActiveFilter> = [
            ...activeFilters,
            {
                filterId: filter.id,
                type: filter.type,
                values:
                    filter.type === FilterTypes.datepicker_range ||
                    filter.type === FilterTypes.number_range
                        ? [null, null]
                        : filter.type === FilterTypes.score_range ||
                            filter.type === FilterTypes.tags
                          ? []
                          : [],
                booleanValue:
                    filter.type === FilterTypes.boolean ? true : undefined,
            },
        ];
        setValue(TableFormValues.filters, updatedFilters);
        setAnchorEl(null);

        setTimeout(() => {
            const chipElement = document.getElementById(
                `filter-chip-${newFilterIndex}`
            );
            if (chipElement) {
                setValueAnchorEl(chipElement);
            }
            setActiveFilterIndex(newFilterIndex);
        }, 0);
    };

    const handleValueMenuClose = () => {
        setValueAnchorEl(null);
        setActiveFilterIndex(null);

        // Remove filter if no value is selected
        if (activeFilterIndex !== null) {
            const activeFilter = activeFilters[activeFilterIndex];
            if (
                (activeFilter.type === FilterTypes.options &&
                    Array.isArray(activeFilter.values) &&
                    activeFilter.values.length === 0) ||
                (activeFilter.type === FilterTypes.datepicker_range &&
                    activeFilter.values[0] === null &&
                    activeFilter.values[1] === null) ||
                (activeFilter.type === FilterTypes.score_range &&
                    activeFilter.values[0] === 1 &&
                    activeFilter.values[1] === 10) ||
                (activeFilter.type === FilterTypes.number_range &&
                    activeFilter.values[0] === null &&
                    activeFilter.values[1] === null) ||
                (activeFilter.type === FilterTypes.boolean &&
                    activeFilter.booleanValue === undefined) ||
                (activeFilter.type === FilterTypes.tags &&
                    Array.isArray(activeFilter.values) &&
                    activeFilter.values.length === 0)
            ) {
                handleRemoveFilter(activeFilterIndex);
            }
        }
    };

    const handleValueSelect = (value: IOption) => {
        if (activeFilterIndex !== null) {
            const updatedFilters = activeFilters.map((activeFilter, i) =>
                i === activeFilterIndex &&
                filters.find((ft) => ft.id === activeFilter.filterId)?.type ===
                    FilterTypes.options
                    ? {
                          ...activeFilter,
                          values: (activeFilter.values as IOption[]).some(
                              (v) => v.value === value.value
                          )
                              ? (activeFilter.values as IOption[]).filter(
                                    (v) => v.value !== value.value
                                )
                              : [...(activeFilter.values as IOption[]), value],
                      }
                    : activeFilter
            );
            setValue(TableFormValues.filters, updatedFilters);
        }
    };

    const handleChipClick = (
        event: React.MouseEvent<HTMLElement>,
        index: number
    ) => {
        setActiveFilterIndex(index);
        setValueAnchorEl(event.currentTarget);
    };

    const handleRemoveFilter = (index: number) => {
        const updatedFilters = activeFilters.filter((_, i) => i !== index);
        setValue(TableFormValues.filters, updatedFilters);
    };

    const handleBooleanFilterToggle = (value: boolean) => {
        const updatedFilters = activeFilters.map((filter) =>
            filter.filterId === activeFilters[activeFilterIndex].filterId
                ? { ...filter, booleanValue: value }
                : filter
        );
        setValue(TableFormValues.filters, updatedFilters);
    };

    const handleDateRangeChange = (
        newValue: [Moment | null, Moment | null],
        index: number
    ) => {
        const updatedFilters = activeFilters.map((activeFilter, i) =>
            i === index &&
            filters.find((ft) => ft.id === activeFilter.filterId)?.type ===
                FilterTypes.datepicker_range
                ? {
                      ...activeFilter,
                      values: newValue,
                  }
                : activeFilter
        );
        setValue(TableFormValues.filters, updatedFilters);
    };

    const handleScoreRangeChange = (
        newValue: IRangeBlocksValues,
        index: number
    ) => {
        const updatedFilters = activeFilters.map((activeFilter, i) =>
            i === index &&
            filters.find((ft) => ft.id === activeFilter.filterId)?.type ===
                FilterTypes.score_range
                ? {
                      ...activeFilter,
                      values: [newValue.from, newValue.to],
                  }
                : activeFilter
        );
        setValue(TableFormValues.filters, updatedFilters);
    };

    const handleNumberRangeChange = (
        newValue: [number | null, number | null],
        index: number
    ) => {
        const updatedFilters = activeFilters.map((activeFilter, i) =>
            i === index &&
            filters.find((ft) => ft.id === activeFilter.filterId)?.type ===
                FilterTypes.number_range
                ? {
                      ...activeFilter,
                      values: newValue,
                  }
                : activeFilter
        );
        setValue(TableFormValues.filters, updatedFilters);
    };

    const handleTagApply = (tag: ITag) => {
        if (activeFilterIndex === null) return;

        const updatedFilters = [...activeFilters];
        const activeFilter = updatedFilters[activeFilterIndex];

        if (activeFilter.type === FilterTypes.tags) {
            const newValues = [...(activeFilter.values as ITag[]), tag];
            updatedFilters[activeFilterIndex] = {
                ...activeFilter,
                values: newValues,
            };
            setValue(TableFormValues.filters, updatedFilters);
        }
    };

    const handleTagUnapply = (tagId: number) => {
        if (activeFilterIndex === null) return;
        const updatedFilters = [...activeFilters];
        const activeFilter = updatedFilters[activeFilterIndex];

        if (activeFilter.type === FilterTypes.tags) {
            const newValues = (activeFilter.values as ITag[]).filter(
                (t) => t.tag_id !== tagId
            );
            updatedFilters[activeFilterIndex] = {
                ...activeFilter,
                values: newValues,
            };
            setValue(TableFormValues.filters, updatedFilters);
        }
    };

    const handleClearStartDate = (index: number) => {
        const updatedFilters: Array<IActiveFilter> = activeFilters.map(
            (activeFilter, i) =>
                i === index &&
                filters.find(
                    (filterOption) => filterOption.id === activeFilter.filterId
                )?.type === FilterTypes.datepicker_range
                    ? {
                          ...activeFilter,
                          values: [
                              null,
                              (
                                  activeFilter.values as [
                                      Moment | null,
                                      Moment | null,
                                  ]
                              )[1],
                          ],
                      }
                    : activeFilter
        );
        setValue(TableFormValues.filters, updatedFilters);
    };

    const handleClearEndDate = (index: number) => {
        const updatedFilters: Array<IActiveFilter> = activeFilters.map(
            (activeFilter, i) =>
                i === index &&
                filters.find(
                    (filterOption) => filterOption.id === activeFilter.filterId
                )?.type === FilterTypes.datepicker_range
                    ? {
                          ...activeFilter,
                          values: [
                              (
                                  activeFilter.values as [
                                      Moment | null,
                                      Moment | null,
                                  ]
                              )[0],
                              null,
                          ],
                      }
                    : activeFilter
        );
        setValue(TableFormValues.filters, updatedFilters);
    };

    const availableFilterTypes = filters.filter(
        (filter) =>
            !activeFilters.some(
                (activeFilter) => activeFilter.filterId === filter.id
            )
    );

    return (
        <>
            {activeFilters.map((filterItem, index) => {
                const filterType = filters.find(
                    (filter) => filter.id === filterItem.filterId
                );
                return (
                    <Grid item key={index}>
                        <Chip
                            id={`filter-chip-${index}`}
                            label={(() => {
                                if (
                                    filterType?.type ===
                                    FilterTypes.datepicker_range
                                ) {
                                    return `${filterType.label}: ${renderDateRangeLabel(
                                        filterItem.values as [
                                            Moment | null,
                                            Moment | null,
                                        ]
                                    )}`;
                                } else if (
                                    filterType?.type === FilterTypes.score_range
                                ) {
                                    const fromValue = filterItem
                                        .values[0] as number;
                                    const toValue = filterItem
                                        .values[1] as number;
                                    return (
                                        <RangeBlockChipWrapper>
                                            <Typography variant="body2">
                                                {filterType?.label}:
                                            </Typography>
                                            <div
                                                style={{
                                                    flexShrink: 0,
                                                    minWidth: '100px',
                                                }}
                                            >
                                                <RangeBlocks
                                                    externalValue={{
                                                        from: fromValue,
                                                        to: toValue,
                                                    }}
                                                    color={
                                                        theme.palette.primary
                                                            .main
                                                    }
                                                    disabled={true}
                                                />
                                            </div>
                                        </RangeBlockChipWrapper>
                                    );
                                } else if (
                                    filterType?.type ===
                                    FilterTypes.number_range
                                ) {
                                    const fromValue = filterItem
                                        .values[0] as number | null;
                                    const toValue = filterItem.values[1] as
                                        | number
                                        | null;
                                    return `${filterType?.label}: ${
                                        fromValue ?? '–'
                                    } – ${toValue ?? '–'}`;
                                } else if (
                                    filterType?.type === FilterTypes.boolean
                                ) {
                                    return `${filterType?.label}: ${
                                        filterItem.booleanValue
                                            ? t('filters.show')
                                            : t('filters.hide')
                                    }`;
                                } else if (
                                    filterType?.type === FilterTypes.tags
                                ) {
                                    return (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{ pr: 1 }}
                                            >
                                                {filterType?.label}:
                                            </Typography>
                                            {(filterItem.values as ITag[])
                                                .length > 0 ? (
                                                (
                                                    filterItem.values as ITag[]
                                                ).map((tag) => (
                                                    <PargodaTag
                                                        key={tag.tag_id}
                                                        tag={tag}
                                                    />
                                                ))
                                            ) : (
                                                <Typography variant="body2">
                                                    __
                                                </Typography>
                                            )}
                                        </Box>
                                    );
                                } else {
                                    return `${filterType?.label}: ${
                                        (filterItem.values as IOption[]).length
                                            ? (filterItem.values as IOption[])
                                                  .map((option) =>
                                                      option.label.length > 20
                                                          ? `${option.label.slice(
                                                                0,
                                                                20
                                                            )}...`
                                                          : option.label
                                                  )
                                                  .join(', ')
                                            : '__'
                                    }`;
                                }
                            })()}
                            variant="outlined"
                            clickable
                            onClick={(event) => handleChipClick(event, index)}
                            onDelete={() => handleRemoveFilter(index)}
                            deleteIcon={<CloseIcon />}
                            sx={{
                                borderRadius: '37px',
                                height: '48px',
                                color: theme.palette.grey[400],
                                '& .MuiChip-label': {
                                    display: 'flex',
                                    padding: '4px 8px',
                                    width: '100%',
                                },
                            }}
                        />
                    </Grid>
                );
            })}

            <Grid item>
                <StyledChip
                    label={
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                            {t('filters.addFilter')}
                            <Icon
                                name={'AddCircleOutlineOutlinedIcon'}
                                sx={{
                                    marginInlineStart: '8px',
                                    color: theme.palette.secondary.main,
                                    height: '20px',
                                    width: '20px',
                                }}
                            />
                        </span>
                    }
                    variant="outlined"
                    onClick={handleAddFilterClick}
                    clickable
                />
            </Grid>

            <FiltersMenu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                availableFilterTypes={availableFilterTypes}
                handleFilterSelect={handleFilterSelect}
            />
            {/* Options Menu */}
            {activeFilterIndex !== null &&
                filters.find(
                    (ft) => ft.id === activeFilters[activeFilterIndex].filterId
                )?.type === FilterTypes.options && (
                    <Menu
                        anchorEl={valueAnchorEl}
                        open={Boolean(valueAnchorEl)}
                        onClose={handleValueMenuClose}
                        PaperProps={{
                            style: {
                                maxHeight: 240,
                                minWidth: '210px',
                                maxWidth: '360px',
                            },
                        }}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal:
                                theme.direction === Directions.rtl
                                    ? 'right'
                                    : 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal:
                                theme.direction === Directions.rtl
                                    ? 'right'
                                    : 'left',
                        }}
                    >
                        <SelectorMenu
                            activeFilters={activeFilters}
                            filters={filters}
                            activeFilterIndex={activeFilterIndex}
                            handleValueSelect={handleValueSelect}
                        />
                    </Menu>
                )}
            {/* Date Range Menu */}
            {activeFilterIndex !== null &&
                filters.find(
                    (ft) => ft.id === activeFilters[activeFilterIndex].filterId
                )?.type === FilterTypes.datepicker_range && (
                    <Menu
                        anchorEl={valueAnchorEl}
                        open={Boolean(valueAnchorEl)}
                        onClose={handleValueMenuClose}
                        PaperProps={{
                            style: {
                                maxHeight: 240,
                                width: '260px',
                            },
                        }}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal:
                                theme.direction === Directions.rtl
                                    ? 'right'
                                    : 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal:
                                theme.direction === Directions.rtl
                                    ? 'right'
                                    : 'left',
                        }}
                    >
                        <DateRangeMenu
                            activeFilterIndex={activeFilterIndex}
                            activeFilters={activeFilters}
                            handleDateRangeChange={handleDateRangeChange}
                            handleClearStartDate={handleClearStartDate}
                            handleClearEndDate={handleClearEndDate}
                        />
                    </Menu>
                )}
            {/* Score Range Menu */}
            {activeFilterIndex !== null &&
                filters.find(
                    (ft) => ft.id === activeFilters[activeFilterIndex].filterId
                )?.type === FilterTypes.score_range && (
                    <Menu
                        anchorEl={valueAnchorEl}
                        open={Boolean(valueAnchorEl)}
                        onClose={handleValueMenuClose}
                        PaperProps={{
                            style: {
                                maxHeight: 240,
                                width: '260px',
                            },
                        }}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal:
                                theme.direction === Directions.rtl
                                    ? 'right'
                                    : 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal:
                                theme.direction === Directions.rtl
                                    ? 'right'
                                    : 'left',
                        }}
                    >
                        <ScoreRangeMenu
                            activeFilterIndex={activeFilterIndex}
                            activeFilters={activeFilters}
                            onChange={(newValue) =>
                                handleScoreRangeChange(
                                    newValue,
                                    activeFilterIndex
                                )
                            }
                            customRanges={
                                filters.find(
                                    (ft) =>
                                        ft.id ===
                                        activeFilters[activeFilterIndex]
                                            .filterId
                                )?.customRanges
                            }
                        />
                    </Menu>
                )}
            {activeFilterIndex !== null &&
                filters.find(
                    (ft) => ft.id === activeFilters[activeFilterIndex].filterId
                )?.type === FilterTypes.number_range && (
                    <Menu
                        anchorEl={valueAnchorEl}
                        open={Boolean(valueAnchorEl)}
                        onClose={handleValueMenuClose}
                        PaperProps={{
                            style: {
                                maxHeight: 280,
                                width: '260px',
                            },
                        }}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal:
                                theme.direction === Directions.rtl
                                    ? 'right'
                                    : 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal:
                                theme.direction === Directions.rtl
                                    ? 'right'
                                    : 'left',
                        }}
                    >
                        <NumberRangeMenu
                            activeFilterIndex={activeFilterIndex}
                            activeFilters={activeFilters}
                            minValue={
                                filters.find(
                                    (ft) =>
                                        ft.id ===
                                        activeFilters[activeFilterIndex]
                                            .filterId
                                )?.minValue
                            }
                            maxValue={
                                filters.find(
                                    (ft) =>
                                        ft.id ===
                                        activeFilters[activeFilterIndex]
                                            .filterId
                                )?.maxValue
                            }
                            onChange={(newValue) =>
                                handleNumberRangeChange(
                                    newValue,
                                    activeFilterIndex
                                )
                            }
                        />
                    </Menu>
                )}
            {/* Boolean Filter */}
            {activeFilterIndex !== null &&
                filters.find(
                    (ft) => ft.id === activeFilters[activeFilterIndex].filterId
                )?.type === FilterTypes.boolean && (
                    <Menu
                        anchorEl={valueAnchorEl}
                        open={Boolean(valueAnchorEl)}
                        onClose={handleValueMenuClose}
                        PaperProps={{
                            style: {
                                maxHeight: 240,
                                width: '260px',
                            },
                        }}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal:
                                theme.direction === Directions.rtl
                                    ? 'right'
                                    : 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal:
                                theme.direction === Directions.rtl
                                    ? 'right'
                                    : 'left',
                        }}
                    >
                        <BooleanFilterMenu
                            label={activeFilters[activeFilterIndex].filterId}
                            value={
                                activeFilters[activeFilterIndex].booleanValue
                            }
                            onToggle={handleBooleanFilterToggle}
                        />
                    </Menu>
                )}
            {/* Tags Menu */}
            {activeFilterIndex !== null &&
                filters.find(
                    (ft) => ft.id === activeFilters[activeFilterIndex].filterId
                )?.type === FilterTypes.tags && (
                    <Menu
                        anchorEl={valueAnchorEl}
                        open={Boolean(valueAnchorEl)}
                        onClose={handleValueMenuClose}
                        PaperProps={{
                            style: {
                                maxHeight: 480,
                                width: '360px',
                            },
                        }}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal:
                                theme.direction === Directions.rtl
                                    ? 'right'
                                    : 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal:
                                theme.direction === Directions.rtl
                                    ? 'right'
                                    : 'left',
                        }}
                    >
                        <TagsFilterMenu
                            appliedTags={
                                activeFilters[activeFilterIndex]
                                    .values as ITag[]
                            }
                            onTagApply={handleTagApply}
                            onTagUnapply={handleTagUnapply}
                        />
                    </Menu>
                )}
        </>
    );
};

export default FilterComponent;
